import Image from "@tiptap/extension-image";
import { Placeholder } from "@tiptap/extensions";
import { Node } from "@tiptap/pm/model";
import { TextSelection } from "@tiptap/pm/state";
import { createDocument, Editor, EditorContent, EditorContext, ReactNodeViewRenderer, useEditor } from "@tiptap/react";
import StarterKit from "@tiptap/starter-kit";
import classNames from "classnames";
import { useEffect, useMemo, useRef, useState } from "react";
import { useId } from "react-aria";
import { Control, Controller, RegisterOptions } from "react-hook-form";
import { useDispatch } from "react-redux";
import { Label, Loader } from "semantic-ui-react";
import { fieldError } from "../../../../utils/functions";
import { ControlledRichTextEditorMenuBar } from "./components/ControlledRichTextEditorMenuBar";
import { useDisableDefaultDropEvent } from "./hooks/useDisableDefaultDropEvent";
import { checkFeature } from "./util/checkFeature";
import { markdownToRichTextEditorDocumentJSON, tiptapEditorToMarkdown } from "./util/controlledRichTextEditorMarkdown";

import { useStore } from "zustand";
import { coreUploadTempFile, FileUploadFinalizeResponse, FileUsage } from "../../../../actions/coreUpload";
import "./ControlledRichTextEditor.scss";
import { ControlledRichTextEditorAltTextWarning } from "./components/ControlledRichTextEditorAltTextWarning";
import { ControlledRichTextEditorCustomImage } from "./components/ControlledRichTextEditorCustomImage";
import { SelectionDecoration } from "./extensions/SelectionDecoration";
import { Video } from "./extensions/Video";
import {
  ControlledRichTextEditorMenuBarContext,
  createControlledRichTextEditorMenuBarStore,
} from "./providers/ControlledRichTextEditorMenuBarProvider";
import { useDocumentIsMissingAltText } from "./util/useDocumentIsMissingAltText";

export type RichTextEditorFormat = "html" | "markdown" | "json";
export type RichTextEditorFeatureValue = undefined | false | { disableMenuButton?: boolean; disablePlugin?: boolean };
export type RichTextEditorFeatures = {
  bold?: false;
  italic?: false;
  strike?: false;
  heading?: false;
  bulletList?: false;
  orderedList?: false;
  blockquote?: false;
  link?: false;
  media?: RichTextEditorFeatureValue;
  horizontalRule?: false;
  clearFormatting?: false;
  undoRedo?: false;
};
export type RichTextEditorMediaProps =
  | {
      mediaFileUsage: FileUsage;
      addMediaFile: (uuid: string) => void;
    }
  | {
      mediaFileUsage?: undefined;
      addMediaFile?: undefined;
    };

export function RichTextEditor({
  value,
  onChange,
  label,
  error,
  format = "json",
  features: featuresProp,
  placeholder,
  className,
  id,
  hasWhiteMenuBar = false,
  ...mediaProps
}: {
  value: string;
  onChange: (value: string) => void;
  label?: string;
  error?: string;
  format?: RichTextEditorFormat;
  features?: RichTextEditorFeatures;
  placeholder?: string;
  className?: string;
  id?: string;
  hasWhiteMenuBar?: boolean;
} & RichTextEditorMediaProps) {
  const dispatch = useDispatch();
  const editorRef = useRef(null);
  const [menuBarStore] = useState(createControlledRichTextEditorMenuBarStore);
  const { setIsLinkMenuVisible, setLinkDisplayText, setLinkUrl } = useStore(menuBarStore);

  const [isUploadingImage, setIsUploadingImage] = useState(false);

  const features = useMemo(() => {
    if (!featuresProp) return {};
    return featuresProp;
  }, [featuresProp]);

  useDisableDefaultDropEvent(editorRef);

  const transformedValue = useMemo(() => {
    switch (format) {
      case "html":
        return value;
      case "json":
        if (!value) return null;
        return JSON.parse(value);
      case "markdown":
        if (!value) return null;
        return markdownToRichTextEditorDocumentJSON(value);
    }
  }, [value, format]);

  const getContent = (editor: Editor) => {
    switch (format) {
      case "html":
        return editor.getHTML();
      case "json":
        return JSON.stringify(editor.getJSON());
      case "markdown":
        return tiptapEditorToMarkdown(editor);
      default:
        return editor.getHTML();
    }
  };

  const extensions = useMemo(() => {
    return [
      StarterKit.extend({
        priority: 1000,
        addKeyboardShortcuts() {
          // Keyboard shortcuts from the old editor
          return {
            "Control-Shift-1": () => {
              if (checkFeature(features.heading).plugin) {
                this.editor.commands.toggleHeading({ level: 1 });
              }
            },
            "Control-Shift-2": () => {
              if (checkFeature(features.heading).plugin) {
                this.editor.commands.toggleHeading({ level: 2 });
              }
            },
            "Control-Shift-3": () => {
              if (checkFeature(features.heading).plugin) {
                this.editor.commands.toggleHeading({ level: 3 });
              }
            },
            "Control-Shift-4": () => {
              if (checkFeature(features.heading).plugin) {
                this.editor.commands.toggleHeading({ level: 4 });
              }
            },
            "Control-Shift-5": () => {
              if (checkFeature(features.heading).plugin) {
                this.editor.commands.toggleHeading({ level: 5 });
              }
            },
            "Control-Shift-6": () => {
              if (checkFeature(features.heading).plugin) {
                this.editor.commands.toggleHeading({ level: 6 });
              }
            },
            "Control-Shift-8": () => {
              if (checkFeature(features.bulletList).plugin) {
                this.editor.commands.toggleBulletList();
              }
            },
            "Control-Shift-9": () => {
              if (checkFeature(features.orderedList).plugin) {
                this.editor.commands.toggleOrderedList();
              }
            },
            "Mod-]": () => {
              if (checkFeature(features.blockquote).plugin) {
                this.editor.commands.toggleBlockquote();
              }
            },
            "Mod-Shift-k": () => {
              const isEditingLink = this.editor.isActive("link");
              if (isEditingLink) {
                editor.chain().focus().extendMarkRange("link").enableSelectionDecoration().run();
                const selectedText = editor.state.doc.textBetween(
                  editor.state.selection?.from,
                  editor.state.selection?.to,
                  " ",
                );
                const linkUrl = this.editor.getAttributes("link").href;
                setIsLinkMenuVisible(true);
                setLinkUrl(linkUrl || "");
                setLinkDisplayText(selectedText || "");
              }
            },
            "Mod-k": () => {
              if (checkFeature(features.link).plugin) {
                const isEditingLink = this.editor.isActive("link");
                if (isEditingLink) {
                  this.editor.commands.unsetLink();
                } else {
                  const selectedText = editor.state.doc.textBetween(
                    editor.state.selection?.from,
                    editor.state.selection?.to,
                    " ",
                  );
                  setIsLinkMenuVisible(true);
                  setLinkDisplayText(selectedText || "");
                  editor.chain().focus().enableSelectionDecoration().run();
                }
              }
            },
            "Mod-\\": () => {
              if (checkFeature(features.clearFormatting).plugin) {
                this.editor.commands.unsetAllMarks();
              }
            },
          };
        },
      }).configure({
        underline: false,
        codeBlock: false,
        code: false,
        bold: checkFeature(features.bold).plugin ? undefined : false,
        italic: checkFeature(features.italic).plugin ? undefined : false,
        strike: checkFeature(features.strike).plugin ? undefined : false,
        heading: checkFeature(features.heading).plugin ? undefined : false,
        bulletList: checkFeature(features.bulletList).plugin ? undefined : false,
        orderedList: checkFeature(features.orderedList).plugin ? undefined : false,
        blockquote: checkFeature(features.blockquote).plugin ? undefined : false,
        horizontalRule: checkFeature(features.horizontalRule).plugin ? undefined : false,
        link: checkFeature(features.link).plugin
          ? { openOnClick: false, autolink: false, enableClickSelection: true }
          : false,
        undoRedo: checkFeature(features.undoRedo).plugin ? undefined : false,
      }),
      ...(checkFeature(features.media).plugin
        ? [
            Image.configure({
              inline: false,
            }).extend({
              addAttributes() {
                return {
                  ...(this as any).parent?.(),
                  isDecorative: {
                    default: false,
                    parseHTML: (element: HTMLElement) => element.getAttribute("data-is-decorative") === "true",
                  },
                };
              },
              addNodeView() {
                return ReactNodeViewRenderer(ControlledRichTextEditorCustomImage);
              },
            }),
            Video,
          ]
        : []),
      Placeholder.configure({
        placeholder,
      }),
      SelectionDecoration,
    ];
  }, [features, placeholder]);

  const editor = useEditor({
    extensions,
    content: transformedValue,
    onUpdate: ({ editor }) => {
      onChange(getContent(editor));
    },
    editorProps: {
      handleDOMEvents: {
        ...(checkFeature(features.media).anyDisabled
          ? {
              drop: (_view, event) => {
                event.preventDefault();
                event.stopPropagation();
                return false;
              },
            }
          : {}),
      },
      handleDrop(view, event, _slice, moved) {
        const isFileUpload = !moved && event.dataTransfer && event.dataTransfer.files && event.dataTransfer.files[0];
        if (isFileUpload && checkFeature(features.media).plugin) {
          const allowedImageTypes = ["image/jpeg", "image/png", "image/gif", "image/webp"];

          const file = event.dataTransfer.files[0];
          const isImage = allowedImageTypes.includes(file.type);

          if (!isImage) return false;

          uploadImage(file).then((imageUrl) => {
            const { schema } = view.state;
            const coordinates = view.posAtCoords({ left: event.clientX, top: event.clientY });
            if (!coordinates) return;
            const node = schema.nodes.image.create({ src: imageUrl });
            const transaction = view.state.tr.insert(coordinates.pos, node);
            return view.dispatch(transaction);
          });
        }

        return false;
      },
      handlePaste(view, event) {
        const isFileUpload = event.clipboardData && event.clipboardData.files && event.clipboardData.files[0];
        if (isFileUpload && checkFeature(features.media).plugin) {
          const allowedImageTypes = ["image/jpeg", "image/png", "image/gif", "image/webp"];

          const file = event.clipboardData.files[0];
          const isImage = allowedImageTypes.includes(file.type);

          if (!isImage) return false;

          uploadImage(file).then((imageUrl) => {
            const { schema } = view.state;
            const node = schema.nodes.image.create({ src: imageUrl });
            const transaction = view.state.tr.replaceSelectionWith(node);
            return view.dispatch(transaction);
          });
        }

        return false;
      },
    },
  });
  const memoizedEditor = useMemo(() => ({ editor }), [editor]);

  const uploadImage = async (file: File) => {
    // Don't allow user to upload new content if media feature is disabled,
    // old content will still load if only the menu button is disabled.
    if (checkFeature(features.media).anyDisabled) return;

    setIsUploadingImage(true);
    const response = (await dispatch(
      coreUploadTempFile({
        id: `rich-text-editor-${file.name}`,
        file,
        usage: mediaProps.mediaFileUsage,
      }),
    )) as any;
    const payload = response.payload as FileUploadFinalizeResponse;
    mediaProps.addMediaFile!(payload.uuid);
    setIsUploadingImage(false);
    return payload.url;
  };

  useEffect(() => {
    if (editor && value !== getContent(editor)) {
      // Fixes crash where editor is updated whilst being re-rendered.
      // This is because we are treating the editor as a controlled component, when it is not really designed to be one.
      // The requestAnimationFrame defers the update until after the current render is complete. A proper fix would be
      // to refactor this component to be uncontrolled, but we would lose the ability to easily change the content from
      // outside the editor...
      window.requestAnimationFrame(() => {
        if (!value) {
          // Clear the editor
          const transaction = editor.state.tr
            .setSelection(TextSelection.create(editor.state.doc, 0, editor.state.doc.content.size))
            .replaceSelectionWith(editor.schema.nodes.paragraph.create(), false)
            .setMeta("preventUpdate", true)
            .setMeta("addToHistory", false);
          editor.view.dispatch(transaction);
          return;
        }

        const newContent =
          format === "markdown"
            ? Node.fromJSON(editor.schema, transformedValue)
            : createDocument(transformedValue, editor.schema);
        const transaction = editor.state.tr
          .setSelection(TextSelection.create(editor.state.doc, 0, editor.state.doc.content.size))
          .replaceSelectionWith(newContent, false)
          .setMeta("preventUpdate", true)
          .setMeta("addToHistory", false);
        editor.view.dispatch(transaction);
      });
    }
  }, [editor, value, transformedValue]);

  const generatedId = useId();
  const finalId = id ?? generatedId;

  const shouldShowAltTextWarning = useDocumentIsMissingAltText(editor?.state.doc);

  if (!mediaProps.mediaFileUsage && checkFeature(features.media).anyDisabled === false) {
    throw new Error("ControlledRichTextEditor: `mediaFileUsage` prop is required when media feature is enabled");
  }

  return (
    <EditorContext.Provider value={memoizedEditor}>
      {label && (
        <label className='tw-inline-block' htmlFor={finalId}>
          {label}
        </label>
      )}
      <div className='controlled-rich-text-editor tw-space-y-2'>
        <ControlledRichTextEditorMenuBarContext.Provider value={menuBarStore}>
          <ControlledRichTextEditorMenuBar
            format={format}
            features={features}
            hasWhiteMenuBar={hasWhiteMenuBar}
            {...mediaProps}
          />
        </ControlledRichTextEditorMenuBarContext.Provider>
        <div className='tw-relative'>
          <EditorContent
            ref={editorRef}
            id={finalId}
            editor={editor}
            className={classNames(
              "tw-overflow-x-auto tw-rounded-lg tw-bg-white tw-px-[14px] tw-py-[11px] tw-ring-1 tw-ring-border tw-transition-all tw-duration-100 focus-within:tw-ring-[#85b7d9]",
              className,
            )}
            onClick={() => editor?.commands.focus()}
          />
          {isUploadingImage && (
            <div className='tw-pointer-events-none tw-absolute tw-bottom-0 tw-right-0'>
              <Loader active size='tiny' inline className='tw-m-gg-2' />
            </div>
          )}
        </div>
      </div>
      {error && (
        <Label basic prompt pointing='above' aria-live='polite'>
          {error}
        </Label>
      )}
      {shouldShowAltTextWarning && <ControlledRichTextEditorAltTextWarning />}
    </EditorContext.Provider>
  );
}

export function ControlledRichTextEditor({
  control,
  name,
  label,
  errors,
  defaultValue,
  format = "json",
  features,
  placeholder,
  rules,
  required,
  id,
  className,
  hasWhiteMenuBar = false,
  ...mediaProps
}: {
  control: Control;
  name: string;
  label?: string;
  errors?: any;
  defaultValue?: string;
  format?: RichTextEditorFormat;
  features?: RichTextEditorFeatures;
  placeholder?: string;
  rules?: Exclude<RegisterOptions, "valueAsNumber" | "valueAsDate" | "setValueAs">;
  required?: boolean;
  id?: string;
  className?: string;
  hasWhiteMenuBar?: boolean;
} & RichTextEditorMediaProps) {
  const error = fieldError(errors, name);

  return (
    <Controller
      control={control}
      name={name}
      defaultValue={defaultValue}
      rules={{ required: required ? "This field is required" : false, ...rules }}
      render={({ onChange, value }) => (
        <RichTextEditor
          id={id}
          label={label}
          error={error?.content.message}
          value={value}
          onChange={onChange}
          format={format}
          placeholder={placeholder}
          features={features}
          className={className}
          hasWhiteMenuBar={hasWhiteMenuBar}
          {...mediaProps}
        />
      )}
    />
  );
}
